import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronRightIcon } from '@prophecy/icons';
import { stopPropagation } from '@prophecy/utils/dom';
import { useFocusHandleOnClose, useFocusTrap } from '@prophecy/utils/react/focus';
import { useControlledState } from '@prophecy/utils/react/hooks';
import { Arrow, CheckboxItem, Group, ItemIndicator, Portal, RadioGroup, RadioItem, Sub, Root } from '@radix-ui/react-dropdown-menu';
import React, { createContext, useContext, useRef } from 'react';
import { Stack, StackItem } from '../Layout';
import { Content, DropdownFooterContainer, Item, Label, OverlayContent, Separator, StyledTrigger, SubContent, SubTrigger } from './styled';
import { tokens } from './tokens';
import { Size } from './types';
export const TriggerContext = createContext(false);
export function useTriggerContext() {
    return useContext(TriggerContext);
}
export function Dropdown({ defaultOpen, arrow, disabled, overlay, visible, footer, onVisibleChange, children, overlayClassName, overlayStyle, placement: side = 'bottom', placementOffset: sideOffset = 8, align = 'start', alignOffset, portalled = true, size = Size.md, onCloseAutoFocus, onOpenAutoFocus, onInteractOutside, openOnHover = false, modal = true, 'data-test-id': dataTestId }) {
    const [_visible, toggleVisible] = useControlledState({
        value: visible,
        onChange: onVisibleChange
    });
    const closeTimeoutRef = useRef(null);
    const handleMouseLeave = () => {
        if (!openOnHover)
            return;
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = setTimeout(() => {
            toggleVisible(false);
        }, 300);
    };
    const handleMouseEnter = () => {
        if (!openOnHover)
            return;
        if (closeTimeoutRef.current)
            clearTimeout(closeTimeoutRef.current);
        toggleVisible(true);
    };
    const container = useFocusTrap(Boolean(_visible));
    const defaultOnCloseAutoFocus = useFocusHandleOnClose(Boolean(_visible));
    const content = (_jsx(Content, { className: overlayClassName, style: overlayStyle, side: side, size: size, align: align, sideOffset: sideOffset, asChild: true, alignOffset: alignOffset, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, ref: container, tabIndex: -1, 
        // @ts-ignore: onOpenAutoFocus is internally used but not exposed on types from radix ui
        onOpenAutoFocus: onOpenAutoFocus, onClick: stopPropagation, onCloseAutoFocus: onCloseAutoFocus ?? defaultOnCloseAutoFocus, onInteractOutside: onInteractOutside, "data-test-id": 'dropdown-content', children: _jsxs("div", { children: [arrow && _jsx(Arrow, {}), overlay && _jsx(OverlayContent, { size: size, children: overlay }), footer && _jsx(DropdownFooterContainer, { children: footer })] }) }));
    return (_jsxs(Root, { open: _visible, dir: 'ltr', modal: modal, onOpenChange: toggleVisible, defaultOpen: defaultOpen, children: [_jsx(TriggerContext.Provider, { value: true, children: _jsx(StyledTrigger, { disabled: disabled, asChild: true, "$openOnHover": openOnHover, "data-test-id": dataTestId, onMouseEnter: openOnHover ? handleMouseEnter : undefined, onMouseLeave: openOnHover ? handleMouseLeave : undefined, children: children }) }), portalled ? _jsx(Portal, { children: content }) : content] }));
}
function SubMenu({ children, overlay, triggerArrow = true, overlayArrow, placementOffset: sideOffset = 8, alignOffset, open, defaultOpen, onOpenChange, size = Size.md, ...rest }) {
    return (_jsxs(Sub, { open: open, defaultOpen: defaultOpen, onOpenChange: onOpenChange, children: [_jsx(SubTrigger, { ...rest, size: size, children: _jsxs(Stack, { direction: 'horizontal', width: '100%', gap: tokens.SubMenu.Arrow.gap, alignY: 'center', align: 'space-between', children: [_jsx(StackItem, { children: children }), _jsx(StackItem, { shrink: '0', children: triggerArrow && _jsx(ChevronRightIcon, { type: 'default', size: 'xs' }) })] }) }), _jsxs(SubContent, { "data-test-id": 'dropdown-sub-menu-content', size: size, sideOffset: sideOffset, alignOffset: alignOffset, children: [overlay && _jsx(OverlayContent, { size: size, children: overlay }), overlayArrow && _jsx(Arrow, {})] })] }));
}
// to disable onClick when disabled is true
const DropdownItem = React.forwardRef(({ disabled, onClick, children, ...props }, ref) => {
    const handleClick = disabled ? undefined : onClick;
    return (_jsx(Item, { ...props, ref: ref, onClick: handleClick, disabled: disabled, children: children }));
});
DropdownItem.displayName = 'DropdownItem';
Dropdown.Label = Label;
Dropdown.Item = DropdownItem;
Dropdown.Group = Group;
Dropdown.CheckboxItem = CheckboxItem;
Dropdown.RadioGroup = RadioGroup;
Dropdown.RadioItem = RadioItem;
Dropdown.ItemIndicator = ItemIndicator;
Dropdown.Separator = Separator;
Dropdown.SubMenu = SubMenu;
export { DropdownFooterContainer } from './styled';
export * from './types';
